import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import {
    calendarEventsInEventsCacheReloaded,
    updateFullCalendarEventFromServer,
} from '../../actions/eventsCacheActions';
import { DateRange } from 'owa-datetime-utils';
import { getAllLockedDateRanges } from '../../selectors/eventsCacheSelectors';
import { getDateRangesBeingFetchedForFolder } from '../lockedCalendarEvents/fetchCalendarEventsForDateRange';
import { loadCalendarEventsInDateRange } from '../../services/loadCalendarEventsInDateRange';
import { loadingCalendarEvents } from '../../actions/publicActions';
import { MAX_RETRIES_TO_LOAD_EVENTS_FOR_DATERANGE } from '../../constants';
import { mergeAllOverlappingDateRanges } from '../mergeAllOverlappingDateRanges';
import { getFullItemLoadState, FullItemLoadState } from 'owa-calendar-events-store';

export async function reloadCalendarEventsCache(
    folderId: string,
    forceReload: boolean = false
): Promise<CalendarEvent[]> {
    const dateRangesToReload = getAllDateRangesForFolderIdToBeReloaded(folderId);
    const allFetchPromises = dateRangesToReload.map(dateRange =>
        loadCalendarEventsInDateRange(
            dateRange,
            folderId,
            loadingCalendarEvents,
            MAX_RETRIES_TO_LOAD_EVENTS_FOR_DATERANGE
        )
    );

    const results = await Promise.all(allFetchPromises);

    if (results) {
        // We raise the action that events were reloaded only if the reload result is non-null
        // null result is indication of service call failure and we want to avoid surface going blank in that case
        results.forEach(result => {
            if (result) {
                calendarEventsInEventsCacheReloaded(folderId, result, forceReload);

                // update the full items, if necessary for any item from the result
                result.forEach(
                    item =>
                        getFullItemLoadState(item.ItemId.Id) == FullItemLoadState.Loaded &&
                        updateFullCalendarEventFromServer(item)
                );
            }
        });

        const loadedCalendarEvents = results.reduce<CalendarEvent[]>((agg, events) => {
            // if one of the fetch promises returned null, then the reduce function fails and hence this null check is required.
            if (events) {
                agg.push(...events);
            }
            return agg;
        }, []);

        return loadedCalendarEvents;
    } else {
        return null;
    }
}

function getAllDateRangesForFolderIdToBeReloaded(folderId: string): DateRange[] {
    const dateRangesBeingFetched = getDateRangesBeingFetchedForFolder(folderId);
    const lockedDateRanges = getAllLockedDateRanges(folderId);
    const allDateRanges = [...dateRangesBeingFetched, ...lockedDateRanges];
    const mergedLockedDateRanges = mergeAllOverlappingDateRanges(allDateRanges);

    return mergedLockedDateRanges;
}
